/**
 * The REST a workspace host speaks (backend apps/sandbox/views.py, #872): its lease (take it, keep
 * it, give it up) and its deltas, sent as one batch on the session's seqs (C7).
 */
import { isAxiosError } from "axios";

import api, { UPLOAD_REQUEST_TIMEOUT_MS } from "@/services/api";

import { SandboxError } from "../core/errors";
import type { DeltaOp, DeltaTransport } from "./session";

const GZIP = "application/gzip";

const workspacePath = (workspaceId: string) =>
  `/agent/sandbox/workspaces/${encodeURIComponent(workspaceId)}`;

/** The fencing epoch every write carries, and the last seq the server applied in it. */
export interface Lease {
  epoch: number;
  seq: number;
  expires_at: string | null;
}

/** Another host holds the workspace (`lease_held`), or this one no longer does (`lease_lost`). */
export class LeaseError extends Error {
  constructor(
    readonly code: "lease_held" | "lease_lost",
    message: string
  ) {
    super(message);
    this.name = "LeaseError";
  }
}

/**
 * The server refused these changes as they are (400, 422, a 409 other than the lease or seq ones,
 * a 413 other than a full workspace); `reason` is its error code, never a value.
 */
export class SyncRefused extends SandboxError {
  constructor(
    readonly reason: string,
    message: string
  ) {
    super("EINVAL", message);
  }
}

/**
 * The server would not take any changes from this host now (401, 403 and other 4xx): not the
 * changes' fault, so they wait. A 404 is a lost lease (LeaseError), a non-owner's too.
 */
export class SyncDenied extends SandboxError {
  constructor(
    readonly status: number,
    message: string
  ) {
    super("EFETCH", message);
  }
}

/** The server applied up to `expectedSeq - 1`: the batch did not follow the last seq it applied. */
export class SeqConflict extends Error {
  constructor(
    readonly expectedSeq: number,
    message: string
  ) {
    super(message);
    this.name = "SeqConflict";
  }
}

/** Statuses worth sending again: the server or the way to it failed, not the request. */
const TRANSIENT = (status: number) => status >= 500 || status === 408 || status === 429;

export interface LeaseApi {
  acquire(workspaceId: string, owner: string): Promise<Lease>;
  heartbeat(workspaceId: string, owner: string, epoch: number): Promise<Lease>;
  release(workspaceId: string, owner: string, epoch: number): Promise<void>;
}

/**
 * A refusal as the host acts on it: a lease conflict, a seq out of order, a full workspace
 * (ENOSPC), changes the server refuses as they are (SyncRefused), a host it takes nothing from
 * now (SyncDenied), or a failure worth trying again (EFETCH). A workspace that is gone (404: the
 * user cleared the conversation, which deletes it) is a lost lease, so the host drops it at once.
 */
function refused(error: unknown): never {
  if (isAxiosError(error) && error.response) {
    const { status, data } = error.response;
    const details = (
      data as { details?: { code?: unknown; detail?: unknown; expected_seq?: unknown } } | undefined
    )?.details;
    const message = typeof details?.detail === "string" ? details.detail : `HTTP ${status}`;
    if (status === 409 && (details?.code === "lease_held" || details?.code === "lease_lost")) {
      throw new LeaseError(details.code, message);
    }
    if (status === 404) throw new LeaseError("lease_lost", message);
    // DRF sends the expected seq as a string.
    const expectedSeq = Number(details?.expected_seq);
    if (
      status === 409 &&
      details?.code === "seq" &&
      Number.isSafeInteger(expectedSeq) &&
      expectedSeq > 0
    ) {
      throw new SeqConflict(expectedSeq, message);
    }
    if (status === 413 && details?.code === "workspace_full") {
      throw new SandboxError("ENOSPC", `ENOSPC: ${message}`);
    }
    if (TRANSIENT(status)) {
      throw new SandboxError("EFETCH", `EFETCH: the sandbox's sync was refused (${message})`);
    }
    if ([400, 409, 413, 422].includes(status)) {
      const reason =
        typeof details?.code === "string" && /^[a-z_]{1,64}$/.test(details.code)
          ? details.code
          : `HTTP ${status}`;
      throw new SyncRefused(reason, `EINVAL: the sandbox's sync was refused (${message})`);
    }
    throw new SyncDenied(status, `EFETCH: the server took no changes (HTTP ${status})`);
  }
  throw new SandboxError(
    "EFETCH",
    `EFETCH: the sandbox could not reach the server (${error instanceof Error ? error.message : String(error)})`
  );
}

export const leaseApi: LeaseApi = {
  async acquire(workspaceId, owner) {
    const { data } = await api
      .post<Lease>(`${workspacePath(workspaceId)}/lease/`, { owner })
      .catch(refused);
    return data;
  },
  async heartbeat(workspaceId, owner, epoch) {
    const { data } = await api
      .post<Lease>(`${workspacePath(workspaceId)}/lease/heartbeat/`, { owner, epoch })
      .catch(refused);
    return data;
  },
  async release(workspaceId, owner, epoch) {
    await api.post(`${workspacePath(workspaceId)}/lease/release/`, { owner, epoch }).catch(refused);
  },
};

/** The batch endpoint's manifest entry for one op; a put names the file part holding its bytes. */
function manifestOp(op: DeltaOp, part: string) {
  if (op.op === "put")
    return { seq: op.seq, op: op.op, path: op.path, sha256: op.sha256, mtime: op.mtime, part };
  if (op.op === "delete") return { seq: op.seq, op: op.op, path: op.path };
  return { seq: op.seq, op: op.op, path: op.path, size: op.size };
}

/** Deltas over `deltas/batch/`: applied whole or refused whole. */
export function batchTransport(workspaceId: string): DeltaTransport {
  return {
    async putBatch(epoch, ops) {
      const form = new FormData();
      const manifest = { epoch, ops: ops.map((op, index) => manifestOp(op, `f${index}`)) };
      form.append("manifest", JSON.stringify(manifest));
      ops.forEach((op, index) => {
        if (op.op === "put")
          form.append(`f${index}`, new Blob([op.body as BlobPart], { type: GZIP }), `f${index}.gz`);
      });
      const { data } = await api
        .post<{ seq: number }>(`${workspacePath(workspaceId)}/deltas/batch/`, form, {
          timeout: UPLOAD_REQUEST_TIMEOUT_MS,
          headers: { "Content-Type": "multipart/form-data" },
        })
        .catch(refused);
      return { seq: data.seq };
    },
  };
}
